import {
  AltMessageItem,
  AnswerDefinition,
  BuiltInTransformers,
  OrdChatFlowDefinition,
  PromptDefinition,
  PromptMessageItem,
} from "@xflr6/chatbot";
import { clone } from "ramda";
import { v4 as uuid, v4 } from "uuid";

import { MCFlowEditorError } from "../../errors";
import { WithKey } from "./types";

/**
 * Inserts a prompt into the chat flow definition. Its name is auto-generated.
 * @param definition - If null, a stub definition is auto-generated and used
 * @param index - If null, the prompt is added at the very end of the prompts
 *   array in the chat flow definition.
 * @param flowDef
 * @returns the auto-generated name and the definition of the prompt created
 */
export function insertPrompt(
  definition: PromptDefinition | null,
  index: number | null,
  flowDef: OrdChatFlowDefinition
): { name: string; definition: PromptDefinition } {
  const effectiveDefinition =
    definition != null
      ? clone(definition)
      : {
          message: "",
          normalizeAnswers: [
            { name: BuiltInTransformers.lowercase },
            { name: BuiltInTransformers.trimWhitespace },
            { name: BuiltInTransformers.collapseWhitespace },
          ],
          forbidsAnswerAlteration: true,
        };

  const answers = effectiveDefinition.answers;
  if (answers != null && Array.isArray(answers)) {
    answers.forEach((answerDef) => {
      (answerDef as WithKey<AnswerDefinition>).__key = v4();
    });
  }

  const messages = effectiveDefinition.messages;
  if (messages != null) {
    messages.forEach((messageItemDef) => {
      (messageItemDef as WithKey<PromptMessageItem>).__key = v4();
    });
  }

  const altMessages = effectiveDefinition.quickResponses;
  if (altMessages != null) {
    altMessages.forEach((altMessageItemDef) => {
      (altMessageItemDef as WithKey<AltMessageItem>).__key = v4();
    });
  }

  const item = {
    name: flowDef.promptsArray.length === 0 ? "start" : uuid(),
    definition: effectiveDefinition,
  };
  if (index == null) {
    flowDef.promptsArray.push(item);
  } else if (index > flowDef.promptsArray.length) {
    throw new MCFlowEditorError(`Prompt index ${index} out of bounds`);
  } else {
    flowDef.promptsArray.splice(index, 0, item);
  }

  return item;
}
